/**
 * Application Constants
 *
 * Centralized constants for consistent values across the application.
 */

const MB_IN_BYTES = 1024 * 1024;
const GB_IN_BYTES = 1024 * 1024 * 1024;
const DEFAULT_MAX_UPLOAD_SIZE_MB = 1024; // 1GB

const parseSizeToBytes = (value: string | undefined, fallbackMb: number): number => {
  if (!value) {
    return fallbackMb * MB_IN_BYTES;
  }

  const raw = value.trim().toLowerCase();
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*([kmgt]?b?)?$/);
  if (!match) {
    return fallbackMb * MB_IN_BYTES;
  }

  const amount = Number(match[1]);
  const unit = (match[2] || "m").replace(/b$/, "");

  const multipliers: Record<string, number> = {
    "": MB_IN_BYTES,
    k: 1024,
    m: MB_IN_BYTES,
    g: GB_IN_BYTES,
    t: GB_IN_BYTES * 1024,
  };

  const multiplier = multipliers[unit];
  if (!multiplier || Number.isNaN(amount) || amount <= 0) {
    return fallbackMb * MB_IN_BYTES;
  }

  return Math.ceil(amount * multiplier);
};

const formatSizeLabel = (bytes: number): string => {
  if (bytes % GB_IN_BYTES === 0) {
    return `${bytes / GB_IN_BYTES}GB`;
  }
  return `${Math.round(bytes / MB_IN_BYTES)}MB`;
};

const env = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env;
const MAX_UPLOAD_FILE_SIZE_BYTES = parseSizeToBytes(
  env.VITE_MAX_UPLOAD_SIZE_MB,
  DEFAULT_MAX_UPLOAD_SIZE_MB
);

// File Upload Constants
export const FILE_UPLOAD = {
  MAX_FILE_SIZE: MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL: formatSizeLabel(MAX_UPLOAD_FILE_SIZE_BYTES),
  ACCEPTED_FORMATS: [
    "audio/mpeg", "audio/wav", "audio/m4a", "audio/mp4", "audio/aac", "audio/x-aac",
    "video/mp4", "video/quicktime", "video/x-matroska",
    "audio/webm"
  ],
  ACCEPTED_EXTENSIONS: [".mp3", ".wav", ".m4a", ".mp4", ".webm", ".mov", ".mkv", ".aac"],
} as const;

// Language Options
export const LANGUAGES = {
  en: { label: "English", value: "en" },
  zh: { label: "Chinese (Mandarin)", value: "zh" },
  yue: { label: "Chinese (Cantonese)", value: "yue" },
} as const;

// Language Variants
export const LANGUAGE_VARIANTS = {
  simplified: { label: "Simplified", value: "simplified" },
  traditional: { label: "Traditional", value: "traditional" },
  oral: { label: "Oral/Spoken", value: "oral" },
  written: { label: "Written", value: "written" },
} as const;

// Speaker Diarization Settings
export const SPEAKER_SETTINGS = {
  MIN_SPEAKERS: 1,
  MAX_SPEAKERS: 100,
  DEFAULT_MIN: 2,
  DEFAULT_MAX: 10,
} as const;

// UI Constants
export const UI = {
  // Toast duration
  TOAST_DURATION: 5000,

  // Debounce delays
  SEARCH_DEBOUNCE: 300,
  INPUT_DEBOUNCE: 500,

  // Pagination
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

// Polling intervals (milliseconds)
export const POLLING = {
  MEETING_STATUS: 3000,      // Meeting processing status
  REFINEMENT_STATUS: 2000,   // Section refinement status
  HEALTH_CHECK: 30000,       // System health check
  COST_REFRESH: 30000,       // Cost data refresh
  STALE_TIME: 10000,         // React Query stale time (when to consider data stale)
  CACHE_TIME: 300000,        // React Query cache time (5 min)
} as const;

// Meeting Status
export const MEETING_STATUS = {
  RECORDING: "recording",
  UPLOADED: "uploaded",
  CONVERTING: "converting",
  TRANSCRIBING: "transcribing",
  ANALYZING: "analyzing",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

export type MeetingStatus = typeof MEETING_STATUS[keyof typeof MEETING_STATUS];

// Meeting Status Display
export const MEETING_STATUS_DISPLAY: Record<MeetingStatus, { label: string; color: string }> = {
  [MEETING_STATUS.RECORDING]: {
    label: "Recording",
    color: "text-sky-500",
  },
  [MEETING_STATUS.UPLOADED]: {
    label: "Queued",
    color: "text-gray-500",
  },
  [MEETING_STATUS.CONVERTING]: {
    label: "Converting Audio",
    color: "text-yellow-500",
  },
  [MEETING_STATUS.TRANSCRIBING]: {
    label: "Transcribing",
    color: "text-blue-500",
  },
  [MEETING_STATUS.ANALYZING]: {
    label: "Analyzing",
    color: "text-purple-500",
  },
  [MEETING_STATUS.COMPLETED]: {
    label: "Completed",
    color: "text-green-500",
  },
  [MEETING_STATUS.FAILED]: {
    label: "Failed",
    color: "text-red-500",
  },
};

// API Endpoints (relative paths)
export const API_ENDPOINTS = {
  // Auth
  LOGIN: "/api/v1/auth/login",
  REGISTER: "/api/v1/auth/register",
  LOGOUT: "/api/v1/auth/logout",
  ME: "/api/v1/auth/me",

  // Meetings
  MEETINGS: "/api/v1/meetings",
  MEETING_BY_ID: (id: string) => `/api/v1/meetings/${id}`,
  MEETING_AUDIO: (id: string) => `/api/v1/meetings/${id}/audio`,

  // Admin
  ADMIN_PROVIDERS: "/api/v1/admin/providers",
  ADMIN_COSTS_SUMMARY: "/api/v1/admin/costs/summary",
  ADMIN_COSTS_BY_PROVIDER: "/api/v1/admin/costs/by-provider",
  ADMIN_COSTS_BY_USER: "/api/v1/admin/costs/by-user",
  ADMIN_COSTS_RECENT: "/api/v1/admin/costs/recent",
} as const;

// Model Tier Colors (for UI display)
export const MODEL_TIER_COLORS = {
  high: "text-purple-600",
  medium: "text-blue-600",
  fast: "text-green-600",
  free: "text-emerald-600",
} as const;

export type ModelTier = keyof typeof MODEL_TIER_COLORS;

// Fallback Model Metadata (used when API data is not available)
export const FALLBACK_MODEL_METADATA: Record<string, {
  description: string;
  tier: ModelTier;
  tierLabel: string;
}> = {
  // OpenAI models
  "gpt-4o": {
    description: "Flagship multimodal model, best for complex analysis",
    tier: "high",
    tierLabel: "Premium",
  },
  "gpt-4o-mini": {
    description: "Fast and affordable, good for most tasks",
    tier: "fast",
    tierLabel: "Recommended",
  },
  "gpt-4-turbo": {
    description: "Previous generation flagship, very capable",
    tier: "high",
    tierLabel: "Premium",
  },
  // Qwen models
  "qwen-max": {
    description: "Most capable Qwen model for complex reasoning",
    tier: "high",
    tierLabel: "Premium",
  },
  "qwen-plus": {
    description: "Balanced performance and cost",
    tier: "medium",
    tierLabel: "Balanced",
  },
  "qwen-turbo": {
    description: "Fast responses for simple tasks",
    tier: "fast",
    tierLabel: "Fast",
  },
  // Whisper models
  "whisper-1": {
    description: "OpenAI's powerful speech recognition",
    tier: "high",
    tierLabel: "Premium",
  },
  "whisper-large-v3": {
    description: "Groq's fastest Whisper implementation",
    tier: "fast",
    tierLabel: "Fast",
  },
  // Qwen ASR models
  "qwen3-asr-flash": {
    description: "Qwen ASR Flash for fast transcription",
    tier: "fast",
    tierLabel: "Fast",
  },
  "qwen-asr": {
    description: "Qwen ASR for speech recognition",
    tier: "fast",
    tierLabel: "Fast",
  },
  "qwen3-asr": {
    description: "Qwen ASR for speech recognition",
    tier: "fast",
    tierLabel: "Fast",
  },
  // Ollama local models
  "gpt-oss:20b": {
    description: "Fast local model, zero cost",
    tier: "free",
    tierLabel: "Free",
  },
  "gpt-oss:120b": {
    description: "Large local model, zero cost",
    tier: "free",
    tierLabel: "Free",
  },
  // Local STT model
  "mlx-whisper-large-v3": {
    description: "Local MLX Whisper, zero cost",
    tier: "free",
    tierLabel: "Free",
  },
  // Local diarization model
  "pyannote-local": {
    description: "Local Pyannote diarization, zero cost",
    tier: "free",
    tierLabel: "Free",
  },
};

/** Mirrors backend `agent_max_input_words`; the server stays authoritative. */
export const AGENT_MAX_INPUT_WORDS = 80000;

/** Show the word counter once a prompt gets near the cap. */
export const AGENT_INPUT_WORD_HINT_THRESHOLD = Math.floor(
  AGENT_MAX_INPUT_WORDS * 0.9
);

/** Whitespace-delimited, matching the backend's count. */
export const countWords = (text: string): number =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

// Recording Constants
export const RECORDING = {
  DEFAULT_MAX_DURATION_MS: 5 * 60 * 60 * 1000, // 5 hours
  WARNING_THRESHOLD_MS: 5 * 60 * 1000, // 5 minutes warning
  MIME_TYPE: "audio/webm;codecs=opus",
  FALLBACK_MIME_TYPE: "audio/webm",
  TIMESLICE_MS: 1000, // Data chunk interval
} as const;

// Recording States
export const RECORDING_STATE = {
  IDLE: "idle",
  REQUESTING_PERMISSION: "requesting_permission",
  PERMISSION_DENIED: "permission_denied",
  READY: "ready",
  RECORDING: "recording",
  PAUSED: "paused",
  STOPPED: "stopped",
  ERROR: "error",
} as const;

// Customer support contact
export const SUPPORT_EMAIL = "support@notonlyoffice.ai";

// Public marketing site (logo on unauthenticated pages)
export const MARKETING_SITE_URL = "https://notonlyoffice.ai/en";
